import { openDB } from 'idb';

const DATABASE_PREFIX = 'archeographum-hera-maintenance';
const DATABASE_VERSION = 1;
const connections = new Map();

export const MAINTENANCE_STORES = Object.freeze({
    drafts: 'drafts',
    photos: 'photos',
    outbox: 'outbox',
    referenceData: 'referenceData',
});

const requireScopePart = (value, name) => {
    const normalized = String(value ?? '').trim();
    if (!normalized) {
        throw new Error(`Missing maintenance offline ${name}`);
    }
    return normalized.replace(/[^a-zA-Z0-9_-]/g, '_');
};

export const maintenanceDatabaseName = ({ tenantId, userId }) => [
    DATABASE_PREFIX,
    requireScopePart(tenantId, 'tenantId'),
    requireScopePart(userId, 'userId'),
].join('-');

export const maintenanceDraftKey = ({ sheetName, recordId = 'new' }) => (
    `${requireScopePart(sheetName, 'sheetName')}:${requireScopePart(recordId, 'recordId')}`
);

export async function openMaintenanceDb(scope) {
    const name = maintenanceDatabaseName(scope);
    if (connections.has(name)) {
        return connections.get(name);
    }

    const connection = openDB(name, DATABASE_VERSION, {
        upgrade(db) {
            if (!db.objectStoreNames.contains(MAINTENANCE_STORES.drafts)) {
                const drafts = db.createObjectStore(MAINTENANCE_STORES.drafts, { keyPath: 'key' });
                drafts.createIndex('updatedAt', 'updatedAt');
            }

            if (!db.objectStoreNames.contains(MAINTENANCE_STORES.photos)) {
                const photos = db.createObjectStore(MAINTENANCE_STORES.photos, { keyPath: 'id' });
                photos.createIndex('draftKey', 'draftKey');
                photos.createIndex('createdAt', 'createdAt');
            }

            if (!db.objectStoreNames.contains(MAINTENANCE_STORES.outbox)) {
                const outbox = db.createObjectStore(MAINTENANCE_STORES.outbox, { keyPath: 'id' });
                outbox.createIndex('state', 'state');
                outbox.createIndex('draftKey', 'draftKey', { unique: true });
                outbox.createIndex('updatedAt', 'updatedAt');
            }

            if (!db.objectStoreNames.contains(MAINTENANCE_STORES.referenceData)) {
                db.createObjectStore(MAINTENANCE_STORES.referenceData, { keyPath: 'key' });
            }
        },
        blocking(_currentVersion, _blockedVersion, event) {
            event.target.close();
            connections.delete(name);
        },
        terminated() {
            connections.delete(name);
        },
    });
    connections.set(name, connection);

    try {
        return await connection;
    } catch (error) {
        connections.delete(name);
        throw error;
    }
}

export async function closeMaintenanceDb(scope) {
    const name = maintenanceDatabaseName(scope);
    const connection = connections.get(name);
    if (!connection) return;

    const db = await connection;
    db.close();
    connections.delete(name);
}

const timestamp = () => new Date().toISOString();

export async function putMaintenanceDraft(scope, draft) {
    const db = await openMaintenanceDb(scope);
    const now = timestamp();
    const current = await db.get(MAINTENANCE_STORES.drafts, draft.key);
    const value = {
        ...current,
        ...draft,
        createdAt: current?.createdAt ?? draft.createdAt ?? now,
        updatedAt: now,
    };
    await db.put(MAINTENANCE_STORES.drafts, value);
    return value;
}

export async function getMaintenanceDraft(scope, key) {
    const db = await openMaintenanceDb(scope);
    return db.get(MAINTENANCE_STORES.drafts, key);
}

export async function deleteMaintenanceDraft(scope, key, { deletePhotos = false } = {}) {
    const db = await openMaintenanceDb(scope);
    const transaction = db.transaction(
        [MAINTENANCE_STORES.drafts, MAINTENANCE_STORES.photos, MAINTENANCE_STORES.outbox],
        'readwrite',
    );

    transaction.objectStore(MAINTENANCE_STORES.drafts).delete(key);

    const outboxIndex = transaction.objectStore(MAINTENANCE_STORES.outbox).index('draftKey');
    const outboxCursor = await outboxIndex.openCursor(key);
    if (outboxCursor) {
        await outboxCursor.delete();
    }

    if (deletePhotos) {
        const photoIndex = transaction.objectStore(MAINTENANCE_STORES.photos).index('draftKey');
        let cursor = await photoIndex.openCursor(key);
        while (cursor) {
            await cursor.delete();
            cursor = await cursor.continue();
        }
    }

    await transaction.done;
}

export async function putMaintenancePhoto(scope, photo) {
    const db = await openMaintenanceDb(scope);
    const now = timestamp();
    const value = {
        ...photo,
        id: photo.id ?? crypto.randomUUID(),
        state: photo.state ?? 'local',
        createdAt: photo.createdAt ?? now,
        updatedAt: now,
    };
    await db.put(MAINTENANCE_STORES.photos, value);
    return value;
}

export async function getMaintenancePhoto(scope, id) {
    const db = await openMaintenanceDb(scope);
    return db.get(MAINTENANCE_STORES.photos, id);
}

export async function listMaintenancePhotos(scope, draftKey) {
    const db = await openMaintenanceDb(scope);
    return db.getAllFromIndex(MAINTENANCE_STORES.photos, 'draftKey', draftKey);
}

export async function deleteMaintenancePhoto(scope, id) {
    const db = await openMaintenanceDb(scope);
    await db.delete(MAINTENANCE_STORES.photos, id);
}

export async function putMaintenanceOutbox(scope, entry) {
    const db = await openMaintenanceDb(scope);
    const now = timestamp();
    const existing = entry.id
        ? await db.get(MAINTENANCE_STORES.outbox, entry.id)
        : await db.getFromIndex(MAINTENANCE_STORES.outbox, 'draftKey', entry.draftKey);
    const value = {
        ...existing,
        ...entry,
        id: existing?.id ?? entry.id ?? crypto.randomUUID(),
        state: entry.state ?? existing?.state ?? 'pending',
        retryCount: entry.retryCount ?? existing?.retryCount ?? 0,
        createdAt: existing?.createdAt ?? entry.createdAt ?? now,
        updatedAt: now,
    };
    await db.put(MAINTENANCE_STORES.outbox, value);
    return value;
}

export async function getMaintenanceOutboxForDraft(scope, draftKey) {
    const db = await openMaintenanceDb(scope);
    return db.getFromIndex(MAINTENANCE_STORES.outbox, 'draftKey', draftKey);
}

export async function listMaintenanceOutbox(scope, states = null) {
    const db = await openMaintenanceDb(scope);
    const entries = await db.getAll(MAINTENANCE_STORES.outbox);
    if (!Array.isArray(states) || states.length === 0) {
        return entries;
    }
    const allowed = new Set(states);
    return entries.filter(entry => allowed.has(entry.state));
}

export async function deleteMaintenanceOutbox(scope, id) {
    const db = await openMaintenanceDb(scope);
    await db.delete(MAINTENANCE_STORES.outbox, id);
}

export async function putMaintenanceReferenceData(scope, key, payload) {
    const db = await openMaintenanceDb(scope);
    const value = { key, payload, updatedAt: timestamp() };
    await db.put(MAINTENANCE_STORES.referenceData, value);
    return value;
}

export async function getMaintenanceReferenceData(scope, key) {
    const db = await openMaintenanceDb(scope);
    return db.get(MAINTENANCE_STORES.referenceData, key);
}

export async function estimateMaintenanceStorage() {
    if (typeof navigator === 'undefined' || !navigator.storage?.estimate) {
        return { usage: null, quota: null, ratio: null };
    }
    const { usage = null, quota = null } = await navigator.storage.estimate();
    return {
        usage,
        quota,
        ratio: usage !== null && quota ? usage / quota : null,
    };
}

export async function requestPersistentMaintenanceStorage() {
    if (typeof navigator === 'undefined' || !navigator.storage?.persist) {
        return false;
    }
    return navigator.storage.persist();
}